import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Alert, AlertDescription } from './ui/alert';
import { Skeleton } from './ui/skeleton';
import { Card, CardContent } from './ui/card';
import { Calculator } from 'lucide-react';
import { fetchConversionData, setCurrentUserId } from '../store/slices/conversionSlice';
import ConversionCard from './ConversionCard';
import CurrentOffersCard from './CurrentOffersCard';

const Dashboard = () => {
  const dispatch = useDispatch();
  const { conversionData, loading, error, currentUserId, currentOffers } = useSelector(
    (state) => state.conversion
  );

  const [userIdInput, setUserIdInput] = useState(currentUserId || '');

  useEffect(() => {
    if (currentUserId && !conversionData) {
      dispatch(fetchConversionData(currentUserId));
    }
  }, [currentUserId, conversionData, dispatch]);

  const handleCalculate = () => {
    if (userIdInput && userIdInput !== currentUserId) {
      dispatch(setCurrentUserId(userIdInput));
      dispatch(fetchConversionData(userIdInput));
    }
  };

  return (
    <div className="max-w-7xl mx-auto py-4">
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Input
            className="w-full sm:w-72"
            placeholder="1234567"
            value={userIdInput}
            onChange={(e) => setUserIdInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCalculate()}
          />
          <Button
            size="lg"
            onClick={handleCalculate}
            disabled={loading || !userIdInput}
            className="w-full sm:w-auto gap-2"
          >
            <Calculator className="h-4 w-4" />
            Calculate
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="error" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {(conversionData || loading) && (
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-3">
            <Card className="mb-4 bg-gradient-to-br from-surface-1 to-surface-2">
              <CardContent className="p-4">
                <h3 className="text-center font-bold text-primary-500 text-sm mb-4">
                  User Information
                </h3>
                <div className="flex flex-col gap-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-ink-dim">User ID:</span>
                    <span className="text-sm font-medium text-ink">
                      {loading ? <Skeleton className="w-20 h-5" /> : conversionData?.data?.user_id || currentUserId}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-ink-dim">Email:</span>
                    <span className="text-xs font-medium text-ink max-w-[120px] truncate">
                      {loading ? <Skeleton className="w-24 h-5" /> : conversionData?.data?.email || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-ink-dim">VIP Level:</span>
                    <span className={`text-sm font-bold ${conversionData?.data?.vip_level >= 50 ? 'text-brand-green' : 'text-brand-yellow'}`}>
                      {loading ? <Skeleton className="w-10 h-5" /> : `Level ${conversionData?.data?.vip_level || 0}`}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
            <CurrentOffersCard offers={currentOffers} loading={loading} />
          </div>

          <div className="col-span-12 md:col-span-9">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <ConversionCard
                title="Cashback → Rakeback"
                leftValue={conversionData?.data?.cashback ? (conversionData.data.cashback * 100) : null}
                rightValue={conversionData?.data?.cashback_to_rakeback_perc}
                leftLabel="Cashback" rightLabel="Rakeback" loading={loading}
              />
              <ConversionCard
                title="Rakeback → Cashback"
                leftValue={conversionData?.data?.rakeback ? (conversionData.data.rakeback * 100) : null}
                rightValue={conversionData?.data?.rakeback_to_cashback_perc}
                leftLabel="Rakeback" rightLabel="Cashback" loading={loading}
              />
              <ConversionCard
                title="Rakeback → Reload"
                leftValue={conversionData?.data?.rakeback ? (conversionData.data.rakeback * 100) : null}
                rightValue={conversionData?.data?.rakeback_to_reload_max_reload}
                leftLabel="Rakeback" rightLabel="Reload" loading={loading} isReload={true}
              />
              <ConversionCard
                title="Reload → Rakeback"
                leftValue={conversionData?.data?.reload_perc ? (conversionData.data.reload_perc * 100) : null}
                rightValue={conversionData?.data?.reload_to_rakeback_perc}
                leftLabel="Reload" rightLabel="Rakeback" loading={loading}
              />
              <ConversionCard
                title="Reload → Cashback"
                leftValue={conversionData?.data?.reload_perc ? (conversionData.data.reload_perc * 100) : null}
                rightValue={conversionData?.data?.reload_to_cashback_perc}
                leftLabel="Reload" rightLabel="Cashback" loading={loading}
              />
              <ConversionCard
                title="Cashback → Reload"
                leftValue={conversionData?.data?.cashback ? (conversionData.data.cashback * 100) : null}
                rightValue={conversionData?.data?.cashback_to_reload_max_reload}
                leftLabel="Cashback" rightLabel="Reload" loading={loading} isReload={true}
              />
              <ConversionCard
                title="BMS/JPM/Playgz → Rakeback"
                leftValue="BMS/JPM/Playgz"
                rightValue={conversionData?.data?.other_to_rakeback_perc}
                leftLabel="BMS/JPM/Playgz" rightLabel="Rakeback" loading={loading}
              />
              <ConversionCard
                title="BMS/JPM/Playgz → Cashback"
                leftValue="BMS/JPM/Playgz"
                rightValue={conversionData?.data?.other_to_cashback_perc}
                leftLabel="BMS/JPM/Playgz" rightLabel="Cashback" loading={loading}
              />
              <ConversionCard
                title="BMS/JPM/Playgz → Reload"
                leftValue="BMS/JPM/Playgz"
                rightValue={conversionData?.data?.other_to_reload_max_reload}
                leftLabel="BMS/JPM/Playgz" rightLabel="Reload" loading={loading} isReload={true}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
